import React, { useCallback, useMemo, useState } from 'react';
import { Download, Target } from 'lucide-react';
import { exportOKRsToExcel } from '../lib/exports';
import { ObjectiveCard } from '../features/tracking';
import PersonOKRSearch from '../components/PersonOKRSearch';
import { buildKeyResultsWithOwnerChange, buildKeyResultsWithQuarterEdit } from '../lib/personOkrIndex';

/** Why a reassign was refused, in the admin's words rather than the helper's. */
const REASSIGN_MESSAGES = {
  unchanged: 'That person already owns this IKR.',
  'duplicate-owner': 'That person already owns an IKR under this Team KR. Move or merge it there instead.',
};

export default function TrackingView({ okrs, handleUpdateOkr, handleDeleteOkr, users, teams, viewer, selectedTeamId, allOkrs, searchOkrs, onReassignIkrOwner }) {
  const [personFilterId, setPersonFilterId] = useState(null);
  const searchScope = useMemo(() => searchOkrs || okrs, [searchOkrs, okrs]);

  const handleSelectionChange = useCallback((person) => {
    setPersonFilterId(person ? person.id : null);
  }, []);

  /** Persist a single quarter Actual edited from the person panel. */
  const handleQuarterChange = useCallback((path, quarterIndex, field, value) => {
    const objective = searchScope.find(o => o.id === path.objectiveId);
    const keyResults = buildKeyResultsWithQuarterEdit(objective, path, quarterIndex, field, value, 'FY2526');
    if (keyResults) handleUpdateOkr(path.objectiveId, { keyResults });
  }, [searchScope, handleUpdateOkr]);

  /**
     * Move one IKR to a different owner from the person panel. Admin only.
     * No `accessibleKRs` write for FY25-26: visibility here comes from the team
     * hierarchy plus ownerId, and these key results carry no `code` to grant.
     * The owner's team is copied onto the IKR, matching IndividualKrRow.
     */
  const handleReassign = useCallback(async (path, newOwnerId) => {
    const objective = searchScope.find(o => o.id === path.objectiveId);
    const newOwner = newOwnerId ? users.find(u => (u.uid || u.id) === newOwnerId) : null;
    const result = buildKeyResultsWithOwnerChange(objective, path, newOwnerId, 'FY2526',
      newOwner ? { newOwnerTeamId: newOwner.teamId ?? null } : {});
    if (!result) return { ok: false, message: 'This IKR is no longer available — refresh and try again.' };
    if (!result.ok) return { ok: false, message: REASSIGN_MESSAGES[result.reason] || 'Reassign refused.' };
    return onReassignIkrOwner({
      variant: 'FY2526',
      okrId: path.objectiveId,
      keyResults: result.keyResults,
      previousOwnerId: result.previousOwnerId,
      newOwnerId: newOwnerId || null,
      krCode: null,
    });
  }, [searchScope, users, onReassignIkrOwner]);

  const personSearch = (
    <PersonOKRSearch
      okrs={searchScope}
      users={users}
      variant="FY2526"
      viewerUid={viewer.uid || viewer.id}
      canEditTarget={viewer.role === 'Admin'}
      canEditAllActuals={viewer.role === 'Admin'}
      canReassign={viewer.role === 'Admin'}
      onQuarterChange={handleQuarterChange}
      onReassign={handleReassign}
      onSelectionChange={handleSelectionChange}
    />
  );

  // While a person is selected, their tree replaces the full objective list.
  if (personFilterId) return <div>{personSearch}</div>;

  return (
    <div>
      {personSearch}
      {viewer.role === 'Admin' && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => exportOKRsToExcel(allOkrs || okrs, users, teams)}
            className="flex items-center bg-green-600 text-white font-semibold px-4 py-2 rounded-lg shadow hover:bg-green-700 transition-all"
          >
            <Download size={18} className="mr-2" />
            Export All OKRs
          </button>
        </div>
      )}
      {okrs.map(okr => (
        <ObjectiveCard
          key={okr.id}
          objective={okr}
          onUpdate={handleUpdateOkr}
          onDelete={handleDeleteOkr}
          users={users}
          teams={teams}
          viewer={viewer}
          selectedTeamId={selectedTeamId}
        />
      ))}
      {okrs.length === 0 && (
        <div className="text-center py-20 bg-white rounded-lg shadow-md">
          <Target size={48} className="mx-auto text-gray-300" />
          <h3 className="mt-4 text-xl font-semibold text-gray-700">No Objectives Found</h3>
          <p className="text-gray-500 mt-2">Select a team or &quot;Company Wide&quot; to view objectives.</p>
        </div>
      )}
    </div>
  );
}
